import React from "react";
import { Link } from "react-router-dom";
import useSWR from "swr";
import {
  IoMdAdd,
  IoIosArrowForward,
  IoIosArrowDown,
  IoIosApps,
  IoMdBulb,
  IoIosAlert,
  IoIosAnalytics,
  IoIosCash,
  IoIosCheckmarkCircle,
  IoIosWarning,
  IoIosBook,
} from "react-icons/io";
import { FiArrowUpRight, FiArrowDownLeft } from "react-icons/fi";
import Nav from "../../component/Nav";
import {
  useWalletAmount,
  useCompany,
  useWalletBalances,
  useDebtSummary,
  useCashTotal,
} from "../../api";
import { FormatNumber } from "../../helpers";
import Fab from "../../component/FAB";
import SummaryMonth from "./SummaryMonth";
import TransactionWallet from "./TransactionWallet";
import RecentTransactions from "./RecentTransactions";
import WelcomeHome from "./WelcomeHome";
import "../debt/debt.css";
import "./home.css";

const COLLAPSE_KEY = "homeChartOpen";

const Home = (props) => {
  const { walletAmounts, loading } = useWalletAmount();
  const walletHome = JSON.parse(localStorage.getItem("walletHome"));
  const wallet = walletHome || walletAmounts[0];
  const { balance, isLoading } = useWalletBalances(wallet?.id);
  const { summary } = useDebtSummary();
  const { totalCash } = useCashTotal();
  // Subscribing here ensures auto-init fires on home page (useCompany self-heals on 404).
  useCompany();
  const { data: firstTrx } = useSWR("/transaction?limit=1");
  const [dismissed, setDismissed] = React.useState(
    () => localStorage.getItem("onboardingDismissed") === "1"
  );
  const [chartOpen, setChartOpen] = React.useState(
    () => localStorage.getItem(COLLAPSE_KEY) === "1"
  );

  if (window.location.href.indexOf("id_token") >= 0) return <div />;

  // ── Fresh-user takeover ─────────────────────────────────
  // Show Welcome Home when the user has no activity at all.
  // We need firstTrx to have loaded so we don't flash dashboard then welcome.
  const hasTransaction =
    firstTrx && firstTrx.content && firstTrx.content.length > 0;
  const isFreshUser =
    !dismissed &&
    firstTrx !== undefined &&
    !hasTransaction &&
    summary.openUtangCount === 0 &&
    summary.openPiutangCount === 0 &&
    (totalCash || 0) === 0;
  if (isFreshUser) {
    return <WelcomeHome onDismiss={() => setDismissed(true)} />;
  }

  // Sum balance across all real wallets (skip `additional` marker)
  const totalBalance = walletAmounts.reduce(
    (acc, w) => (w.additional ? acc : acc + (w.sum || 0)),
    0
  );
  const walletCount = walletAmounts.filter((w) => !w.additional && w.sum !== 0)
    .length;

  // Reconciliation: catatan (books) vs kas riil (counted cash).
  // The pill is suppressed when both sides are zero — celebrating 0=0 is noise.
  const delta = Math.round(totalBalance - totalCash);
  const reconciled = delta === 0;
  const showReconPill = !(totalBalance === 0 && totalCash === 0);

  const selectWallet = () => {
    const url = `/wallet/search/${wallet.id}/walletHome/notall`;
    props.history.push(url);
  };

  const toggleChart = () => {
    const next = !chartOpen;
    setChartOpen(next);
    localStorage.setItem(COLLAPSE_KEY, next ? "1" : "0");
  };

  return (
    <>
      <Nav icon={IoMdBulb} />
      <div className="main-padding mb-5">
        {/* ── HERO: reconciliation panel ───────────────────── */}
        <section className="home-hero">
          <div className="home-hero__top">
            <div className="home-hero__label">Saldo Catatan</div>
            {showReconPill && (
              <span
                className={`home-hero__pill home-hero__pill--${
                  reconciled ? "match" : "diff"
                }`}
                onClick={() => props.history.push("/cash")}
                role="button"
                title={reconciled ? "Cocok dengan kas riil" : "Periksa selisih dengan kas riil"}
              >
                {reconciled ? (
                  <>
                    <IoIosCheckmarkCircle />
                    <span>Cocok</span>
                  </>
                ) : (
                  <>
                    <IoIosWarning />
                    <span>
                      Δ {delta > 0 ? "+" : "−"}Rp{" "}
                      {FormatNumber(Math.abs(delta))}
                    </span>
                  </>
                )}
              </span>
            )}
          </div>
          <div
            className={`home-hero__amount ${
              totalBalance < 0 ? "home-hero__amount--negative" : ""
            }`}
          >
            {totalBalance < 0 ? "−" : ""}Rp{" "}
            {FormatNumber(Math.abs(Math.round(totalBalance)))}
          </div>
          <div className="home-hero__sub">
            {walletCount > 0
              ? `${walletCount} wallet aktif`
              : "Belum ada wallet"}
          </div>

          {/* Reconciliation row */}
          <div className="home-recon">
            <button
              type="button"
              className="home-recon__cell"
              onClick={() => props.history.push("/transaction")}
            >
              <span className="home-recon__cell-label">
                <IoIosBook /> Catatan
              </span>
              <span className="home-recon__cell-value">
                Rp {FormatNumber(Math.abs(Math.round(totalBalance)))}
              </span>
            </button>
            <span className="home-recon__divider" aria-hidden />
            <button
              type="button"
              className="home-recon__cell"
              onClick={() => props.history.push("/cash")}
            >
              <span className="home-recon__cell-label">
                <IoIosCash /> Kas Riil
              </span>
              <span className="home-recon__cell-value">
                Rp {FormatNumber(Math.round(totalCash))}
              </span>
            </button>
          </div>

        </section>

        {/* ── Dual debt cards ─────────────────────────────── */}
        <div className="home-debt-grid">
          <Link to="/debts" className="home-debt-card home-debt-card--utang">
            <div className="home-debt-card__head">
              <FiArrowUpRight /> Utang
            </div>
            <div
              className={`home-debt-card__amount ${
                summary.totalUtangOutstanding === 0
                  ? "home-debt-card__amount--zero"
                  : ""
              }`}
            >
              Rp{" "}
              {FormatNumber(Math.round(summary.totalUtangOutstanding))}
            </div>
            <div className="home-debt-card__count">
              {summary.openUtangCount} aktif
            </div>
          </Link>
          <Link
            to="/debts"
            className="home-debt-card home-debt-card--piutang"
          >
            <div className="home-debt-card__head">
              <FiArrowDownLeft /> Piutang
            </div>
            <div
              className={`home-debt-card__amount ${
                summary.totalPiutangOutstanding === 0
                  ? "home-debt-card__amount--zero"
                  : ""
              }`}
            >
              Rp{" "}
              {FormatNumber(Math.round(summary.totalPiutangOutstanding))}
            </div>
            <div className="home-debt-card__count">
              {summary.openPiutangCount} aktif
            </div>
          </Link>
        </div>

        {summary.overdueCount > 0 && (
          <Link
            to="/debts"
            className="home-debt-overdue"
            style={{ textDecoration: "none" }}
          >
            <IoIosAlert size="1.2em" /> {summary.overdueCount} debt telat
            jatuh tempo
            <IoIosArrowForward style={{ marginLeft: "auto" }} />
          </Link>
        )}

        {/* ── Wallet breakdown ────────────────────────────── */}
        <div className="z-card mb-2">
          <div
            className="d-flex justify-content-between align-items-center"
            onClick={(e) => props.history.push("/wallet")}
            style={{ cursor: "pointer" }}
          >
            <div>
              <IoIosApps className="mr-1" />
              <span className="font-weight-bold">Saldo Tiap Wallet</span>
            </div>
            <IoIosArrowForward />
          </div>
          <hr className="mt-1" />
          <div className="column-2">
            <TransactionWallet
              loading={loading}
              data={walletAmounts}
              {...props}
            />
          </div>
        </div>

        {/* ── Recent transactions ─────────────────────────── */}
        <RecentTransactions {...props} />

        {/* ── Collapsible chart ───────────────────────────── */}
        <div className={`home-collapse ${chartOpen ? "home-collapse--open" : ""}`}>
          <button
            type="button"
            className="home-collapse__header"
            onClick={toggleChart}
          >
            <span className="home-collapse__title">
              <IoIosAnalytics /> Transaksi per Bulan
            </span>
            <IoIosArrowDown className="home-collapse__chevron" />
          </button>
          {chartOpen && (
            <div className="home-collapse__body">
              <SummaryMonth
                wallet={wallet}
                data={balance}
                onSelectWallet={selectWallet}
                loading={loading || isLoading}
              />
            </div>
          )}
        </div>
      </div>

      <Fab
        icon={IoMdAdd}
        actions={[
          {
            label: "Catat transaksi",
            icon: IoIosCash,
            onClick: () => {
              localStorage.removeItem("walletSearch");
              props.history.push("/transaction/add");
            },
          },
          {
            label: "Catat utang",
            icon: FiArrowUpRight,
            onClick: () => props.history.push("/debts/add?kind=1"),
          },
          {
            label: "Catat piutang",
            icon: FiArrowDownLeft,
            onClick: () => props.history.push("/debts/add?kind=2"),
          },
        ]}
      />
    </>
  );
};

export default Home;
